const KNOWN_TAGS = [
    'a', 'b', 'blockquote', 'br', 'caption', 'div', 'em', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'i', 'li', 'ol', 'p', 'pre', 'script', 'section', 'span', 'strong', 'style', 'table', 'tbody', 'td',
    'th', 'thead', 'tfoot', 'tr', 'u', 'ul',
].join('|');

const HTML_MARKUP = new RegExp(
    `<\\/?(?:${KNOWN_TAGS})(?=[\\s/>])[^>]*>|&(?:nbsp|amp|lt|gt|quot|apos|#\\d+|#x[\\da-f]+);`,
    'i',
);

const BLOCK_TAGS = new Set([
    'p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'figure', 'table', 'ul', 'ol',
    'section', 'pre', 'tr',
]);

const SKIP_WHITESPACE_PARENTS = new Set([
    'body', 'table', 'tbody', 'thead', 'tfoot', 'ul', 'ol', 'figure', 'div', 'blockquote', 'section',
]);

const isBlockElement = (node: Node): boolean =>
    node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((node as Element).tagName.toLowerCase());

const childrenToText = (nodes: ArrayLike<Node>): string =>
    Array.from(nodes).reduce((acc, child) => {
        const chunk = nodeToText(child);
        if (!chunk) {
            return acc;
        }
        if (isBlockElement(child) && acc.length > 0 && !acc.endsWith('\n')) {
            return `${acc}\n${chunk}`;
        }
        return acc + chunk;
    }, '');

const cellText = (element: Element): string =>
    childrenToText(element.childNodes).replace(/\s+/g, ' ').trim();

const nodeToText = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent ?? '').replace(/\u00a0/g, ' ');
        const parentTag = node.parentElement?.tagName.toLowerCase() ?? '';
        if (SKIP_WHITESPACE_PARENTS.has(parentTag) && text.trim() === '' && /[\n\r]/.test(text)) {
            return '';
        }
        return text;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const element = node as Element;
    const tag = element.tagName.toLowerCase();
    if (tag === 'script' || tag === 'style') {
        return '';
    }
    if (tag === 'br') {
        return '\n';
    }
    if (tag === 'tr') {
        const cells = Array.from(element.children).filter(child => {
            const cellTag = child.tagName.toLowerCase();
            return cellTag === 'td' || cellTag === 'th';
        });
        if (cells.length > 0) {
            return `${cells.map(cellText).join('\t')}\n`;
        }
    }

    const inner = childrenToText(element.childNodes);
    if (BLOCK_TAGS.has(tag)) {
        const content = inner.replace(/\n+$/, '');
        return content ? `${content}\n` : '';
    }
    return inner;
};

export const salesforceHtmlToPlainText = (value: string | null | undefined): string => {
    if (!value || !HTML_MARKUP.test(value)) {
        return value ?? '';
    }
    const parsed = new DOMParser().parseFromString(value, 'text/html');
    return childrenToText(parsed.body.childNodes)
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};
